// Copyright 2025 Sorama B.V.

import { bindable, computedFrom, customElement } from "aurelia-framework";
import { Reportable, ReportableMeasurement } from "datatypes/ReportableMeasurement";
import { SorXData, SorXLDData } from "datatypes/SorXMeasurement";
import { Generic } from "units/Generic";
import { UnitValue } from "units/Unit";
import { SorXLDComputationService } from "./SorXLDComputationService";

@customElement("sorx-ld-overview-template")
export class SorXLDOverviewTemplate {
    @bindable
    public reportListChunk: Reportable[] = [];

    @bindable
    public reportList: Reportable[] = [];

    @bindable
    public pageNumber: number;

    @bindable
    public maxPages: number;

    @bindable
    public lastOverviewPage: number;

    private notAvailableString = "-";

    public get showAssetName(): boolean {
        return this.reportListChunk.some(
            (report) => (report as ReportableMeasurement<SorXLDData & SorXData>).data.tagAssetName
        );
    }

    public get showAssetID(): boolean {
        return this.reportListChunk.some(
            (report) => (report as ReportableMeasurement<SorXLDData & SorXData>).data.tagAssetID
        );
    }

    // Get earliest inspection date from all reports
    @computedFrom("reportListChunk", "reportListChunk.length")
    public get inspectionDate(): Date {
        if (!this.reportListChunk || this.reportListChunk.length === 0) {
            return new Date();
        }
        const dates = this.reportListChunk.map((reportable) => {
            const report = reportable as ReportableMeasurement<SorXLDData>;
            return report.created;
        });

        if (dates.length === 0) {
            return new Date();
        }
        return new Date(Math.min(...dates.map((date) => date.getTime())));
    }

    public getEmissions(reportable: Reportable): string {
        return SorXLDComputationService.estimatedCO2Emission(reportable as ReportableMeasurement<SorXLDData>).toFixed(
            2
        );
    }

    public getCostUnit(): string {
        const firstReport = this.reportListChunk[0] as ReportableMeasurement<SorXLDData>;
        return firstReport?.data.tagLeakCost?.unit?.unit;
    }

    public getLeakRateUnit(): string {
        const firstReport = this.reportListChunk[0] as ReportableMeasurement<SorXLDData>;
        return firstReport?.data.tagLeakRate?.unit?.unit;
    }

    @computedFrom("reportList", "reportList.length")
    public get totalEmissions(): string {
        if (!this.reportList || this.reportList.length === 0) {
            return "N/A";
        }
        return this.reportList
            .map((reportable) =>
                SorXLDComputationService.estimatedCO2Emission(reportable as ReportableMeasurement<SorXLDData>)
            )
            .reduce((total, emission) => {
                return total + emission;
            }, 0)
            .toFixed(2);
    }

    @computedFrom("reportList", "reportList.length")
    public get totalCost(): UnitValue {
        if (!this.reportList || this.reportList.length === 0) {
            return new UnitValue(0, Generic.unitless);
        }
        const total = this.reportList
            .map((reportable) => (reportable as ReportableMeasurement<SorXLDData>).data.tagLeakCost?.value ?? 0)
            .reduce((total, cost) => {
                return total + cost;
            }, 0);
        const firstReport = this.reportList[0] as ReportableMeasurement<SorXLDData>;
        const costUnit = firstReport?.data.tagLeakCost?.unit;
        return new UnitValue(total, costUnit);
    }

    @computedFrom("reportList", "reportList.length")
    public get totalLeakRate(): UnitValue | null {
        if (!this.reportList || this.reportList.length === 0) {
            return null;
        }

        // Check if all reports have the same leak rate unit
        const firstUnit = (this.reportList[0] as ReportableMeasurement<SorXLDData>).data.tagLeakRate?.unit?.unit;
        if (!firstUnit) return null;

        const allSameUnit = this.reportList.every((reportable) => {
            const report = reportable as ReportableMeasurement<SorXLDData>;
            return report.data.tagLeakRate?.unit?.unit === firstUnit;
        });

        if (!allSameUnit) return null;

        const total = this.reportList
            .map((reportable) => this.getLeakRateValue(reportable).value)
            .reduce((sum, val) => sum + val, 0);

        const firstReport = this.reportList[0] as ReportableMeasurement<SorXLDData>;
        return new UnitValue(total, firstReport.data.tagLeakRate.unit);
    }

    public getLeakRateValue(reportable: Reportable): UnitValue {
        const report = reportable as ReportableMeasurement<SorXLDData>;
        return SorXLDComputationService.getLeakRate(report, report.data.tagLeakRate?.unit?.unit);
    }
}
